<template>
  <div class="ugc-list-page">
    <!-- 顶栏 -->
    <div class="top-navbar sticky">
      <div class="nav-item" @click="switchNavTab('home')">
        <div class="nav-icon home-icon"></div>
        <div class="nav-text">{{ langData.home }}</div>
      </div>
      <div class="creation-btn btns" @click="showCreatePopup()">
        <span :class="curLang">{{ langData.creationBtnText }}</span>
      </div>
      <div class="nav-item" @click="switchNavTab('center')">
        <div class="nav-icon center-icon"></div>
        <div class="nav-text">{{ langData.creativeCenter }}</div>
      </div>
    </div>

    <!-- 按钮列表 -->
    <BtnList
      @showRulePopup="showRulePopup"
      @jumpToWebsite="jumpToWebsite"
      @jumpToFacebook="jumpToFacebook"
      @jumpToDiscord="jumpToDiscord"
    />

    <!-- UGC标题 -->
    <div class="ugc-title" :class="curLang"></div>

    <!-- 标签列表 -->
    <div class="tab-list">
      <div
        class="tab tab1"
        :class="{ active: activeUgcTab === UgcTab.UgcTypeMyPoring }"
        @click="switchUgcTab(UgcTab.UgcTypeMyPoring)"
      ><span>{{ langData.createType6 }}</span></div>
      <div
        class="tab tab2"
        :class="{ active: activeUgcTab === UgcTab.UgcTypeJobShare }"
        @click="switchUgcTab(UgcTab.UgcTypeJobShare)"
      ><span>{{ langData.createType7 }}</span></div>
      <div
        class="tab tab3"
        :class="{ active: activeUgcTab === UgcTab.UgcTypePhoto }"
        @click="switchUgcTab(UgcTab.UgcTypePhoto)"
      ><span>{{ langData.createType5 }}</span></div>
    </div>

    <!-- 搜索栏 -->
    <div class="search-bar">
      <div class="search-input-container">
        <input
          type="text"
          class="search-input"
          v-model="searchKeyword"
          :placeholder="langData.searchPlaceholder"
          @keyup.enter="searchUgc"
        />
        <i class="clear-icon" v-if="searchKeyword" @click="clearSearch"></i>
        <i class="empty-clear-icon" v-else></i>
        <i class="line-icon"></i>
        <div class="search-btn" @click="searchUgc">
          {{ langData.search }}
          <i class="search-icon"></i>
        </div>
      </div>
      <div class="filter-panel-container">
        <div class="filter-btn" @click="toggleFilterPanel">
          {{ langData.filter }}
          <i class="filter-icon"></i>
        </div>
        <!-- 筛选面板 -->
        <div class="filter-panel" v-if="showFilterPanel">
          <div class="filter-section">
            <div class="filter-title">{{ langData.sortBy }}</div>
            <div class="filter-options">
              <div
                class="filter-option"
                :class="{ active: sortType === 'new' }"
                @click="setSortType('new')"
              >
                {{ langData.sortByTime }}
              </div>
              <div
                class="filter-option"
                :class="{ active: sortType === 'hot' }"
                @click="setSortType('hot')"
              >
                {{ langData.sortByHot }}
              </div>
            </div>
          </div>
          <div class="filter-section">
            <div class="filter-title">{{ langData.releaseTime }}</div>
            <div class="filter-options">
              <div
                class="filter-option"
                :class="{ active: timeFilter === '' }"
                @click="setTimeFilter('')"
              >
                {{ langData.noLimit }}
              </div>
              <div
                class="filter-option"
                :class="{ active: timeFilter === 'day' }"
                @click="setTimeFilter('day')"
              >
                {{ langData.oneDay }}
              </div>
              <div
                class="filter-option"
                :class="{ active: timeFilter === 'week' }"
                @click="setTimeFilter('week')"
              >
                {{ langData.oneWeek }}
              </div>
            </div>
          </div>
          <div class="filter-actions">
            <div class="filter-action" @click="resetFilters">
              <i class="reset-icon"></i> {{ langData.reset }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- UGC列表 - 虚拟滚动版本 -->
    <div class="ugc-list" v-if="ugcList.length > 0">
      <VirtualGrid
        :items="ugcList"
        :item-key="'ID'"
        :columns="isMobile ? 2 : 4"
        :item-height="562"
        :row-gap="30"
        :buffer-rows="3"
        @reach-bottom="handleVirtualScrollBottom"
      >
        <template #default="{ item }">
          <div
            @click="showContentDetail(item)"
            class="ugc-item-image"
            :style="{
              background: `URL('${
                item?.DISPLAY_PIC || ''
              }') center/contain no-repeat`,
            }"
          >
            <div
              v-if="item.DISPLAY_PIC_LIST && item.DISPLAY_PIC_LIST.length === 0"
              class="ugc-item-content"
            >
              {{ item?.DISPLAY_CONTENT || "" }}
            </div>
          </div>
          <div @click="showContentDetail(item)" class="ugc-item-title">
            {{ item?.DISPLAY_TITLE || "" }}
          </div>
          <div class="ugc-item-meta">
            <div class="ugc-item-info">
              <div
                class="ugc-item-avatar"
                :class="`avatar-${item?.DISPLAY_AVATAR_ID || 1}`"
              ></div>
              <div class="ugc-item-author">
                {{ item?.DISPLAY_LOGIN_ACCOUNT || "" }}
              </div>
            </div>
            <div class="ugc-item-likes">
              <i
                class="like-icon"
                :class="{ liked: +item.IS_LIKE }"
                @click="toggleLike(item.ID)"
              ></i>
              <span class="like-count">{{
                item?.DISPLAY_LIKE_COUNT || item?.LIKE_COUNT || 0
              }}</span>
            </div>
          </div>
        </template>

        <!-- 加载提示：放在虚拟滚动内部 -->
        <template #footer>
          <div class="loading-more" v-if="isLoading && hasMoreData">
            {{ isLoading ? langData.loading : langData.loadingMore }}
          </div>
          <div class="no-more-data" v-if="!hasMoreData && ugcList.length > 0">
            {{ langData.noMoreData }}
          </div>
        </template>
      </VirtualGrid>
    </div>
    <div v-else class="ugc-list">
      <div class="empty-works">
        <div class="empty-text">{{ langData.emptyWorks }}</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, inject, onMounted, onUnmounted } from "vue";
import LanguageHook from "@/hooks/language";
import { getJSON, getActivityApi } from "@/utils";
import BtnList from "@/components/BtnList.vue";
import VirtualGrid from "@/components/VirtualGrid.vue";
import { useRulePopup } from "@/hooks/usePopup";
import { UgcTab } from "@/typings";

// Props & Emits
const emit = defineEmits([
  "goBack",
  "showCreatePopup",
  "switchNavTab",
  "showContentDetail",
  "showLoginTip",
  "showTipMessage",
  "jumpToWebsite",
  "jumpToFacebook",
  "jumpToDiscord",
]);

// 注入的数据
const isLogin = inject("isLogin", ref(false));
const isMobile = inject("isMobile", ref(false)); // 移动端标识

// 注入UGC数据相关（从 home.vue 提供）
const activeUgcTab = inject("activeUgcTab", ref(UgcTab.UgcTypeMyPoring)); // 当前激活的UGC标签
const loadUgcData = inject("loadUgcData");
const ugcList = inject("ugcList", ref([]));
const isLoading = inject("isLoading", ref(false));
const hasMoreData = inject("hasMoreData", ref(true));
const ugcListIndexMap = inject("ugcListIndexMap", ref(new Map())); // 索引映射，用于快速查找
const initActivity = inject("initActivity");
// 规则弹窗 composable
const { showRulePopup: showRulePopupFn } = useRulePopup();

// 语言相关
const { langData, curLang } = LanguageHook();

// 响应式数据（本地状态）
const searchKeyword = ref("");
const showFilterPanel = ref(false);
const selectedFilterType = ref("all");

const sortType = ref("new"); // 排序类型: new-最新, hot-最热门
const timeFilter = ref(""); // 时间筛选: 空-不限, day-一天内, week-一周内

// 筛选类型选项
const filterTypes = ref([
  { value: "all", label: "全部类型" },
  { value: "image", label: "图片" },
  { value: "video", label: "视频" },
  { value: "text", label: "文字" },
]);

// 辅助函数：获取UGC类型文本
const title = {
  [UgcTab.UgcTypeMyROMemory]: "我的RO回憶",
  [UgcTab.UgcTypeCostume]: "時裝設計",
  [UgcTab.UgcTypePhoto]: "美照分享",
  [UgcTab.UgcTypeMyPoring]: "我的波利",
  [UgcTab.UgcTypeJobShare]: "职业分享",
};
const getUgcTypeText = (type) => {
  return title[type];
};

// 页面停留时间追踪
const viewPageStartTime = ref(null);

// 计算浏览时长
const formatDuration = (startTime, endTime) => {
  const duration = Math.floor((endTime - startTime) / 1000); // 转换为秒
  // 格式化为 xxHxxMxxS
  const hours = Math.floor(duration / 3600);
  const minutes = Math.floor((duration % 3600) / 60);
  const seconds = duration % 60;
  return `${hours > 0 ? hours + "H" : ""}${
    minutes > 0 ? minutes + "M" : ""
  }${seconds}S`;
};

// 上报页面停留时间
const reportStayTime = (tabType = null) => {
  if (viewPageStartTime.value) {
    const ugcType = tabType !== null ? tabType : activeUgcTab.value;
    const ugcTypeText = getUgcTypeText(ugcType);
    window.activityDataLayer.push({
      eventName: "stay_time",
      eventType: `UGC创作：${ugcTypeText}`,
      eventValue: formatDuration(viewPageStartTime.value, new Date()),
      aid: window.activity_name,
      gameId: window.game_id,
    });
  }
};

// 方法
const goBack = () => {
  reportStayTime();
  emit("goBack");
};

const showCreatePopup = () => emit("showCreatePopup");

const switchNavTab = (tab) => {
  reportStayTime();
  emit("switchNavTab", tab);
};

const showContentDetail = (item) => emit("showContentDetail", item);

// 虚拟滚动触底加载（代替 home.vue 的 handleScroll）
const loadMoreUgcData = inject("loadMoreUgcData");
const handleVirtualScrollBottom = () => {
  // 调用 home.vue 提供的加载更多函数
  if (loadMoreUgcData) {
    loadMoreUgcData();
  }
};

// 点赞功能 - 使用索引映射优化查找性能（O(1) 代替 O(n)）
const toggleLike = async (id) => {
  if (!isLogin.value) {
    emit("showLoginTip");
    return;
  }

  // 使用 Map 进行 O(1) 快速查找
  const itemIndex = ugcListIndexMap.value.get(id);
  if (
    itemIndex === undefined ||
    itemIndex < 0 ||
    itemIndex >= ugcList.value.length
  ) {
    emit("showTipMessage", langData.commonTip || "操作失败");
    return;
  }

  try {
    const { data, result, code } = await getJSON(
      getActivityApi(window.platform_id, window.activity_name, "like"),
      {
        params: JSON.stringify({
          ugcId: ugcList.value[itemIndex]?.ID,
          likeType: +ugcList.value[itemIndex]?.IS_LIKE ? 2 : 1,
        }),
      }
    );

    if (+result === 1) {
      // 切换点赞状态
      ugcList.value[itemIndex].IS_LIKE = +ugcList.value[itemIndex].IS_LIKE
        ? 0
        : 1;

      if (ugcList.value[itemIndex].IS_LIKE) {
        ugcList.value[itemIndex].LIKE_COUNT++;
        ugcList.value[itemIndex].DISPLAY_LIKE_COUNT =
          +ugcList.value[itemIndex]?.LIKE_COUNT >= 10000
            ? `${(+ugcList.value[itemIndex]?.LIKE_COUNT / 10000).toFixed(1)}W`
            : +ugcList.value[itemIndex]?.LIKE_COUNT >= 1000
            ? `${(+ugcList.value[itemIndex]?.LIKE_COUNT / 1000).toFixed(1)}K`
            : ugcList.value[itemIndex]?.LIKE_COUNT;

        await initActivity();
      } else {
        ugcList.value[itemIndex].LIKE_COUNT = Math.max(
          0,
          ugcList.value[itemIndex].LIKE_COUNT - 1
        );
        ugcList.value[itemIndex].DISPLAY_LIKE_COUNT =
          +ugcList.value[itemIndex]?.LIKE_COUNT >= 10000
            ? `${(+ugcList.value[itemIndex]?.LIKE_COUNT / 10000).toFixed(1)}W`
            : +ugcList.value[itemIndex]?.LIKE_COUNT >= 1000
            ? `${(+ugcList.value[itemIndex]?.LIKE_COUNT / 1000).toFixed(1)}K`
            : ugcList.value[itemIndex]?.LIKE_COUNT;
      }

      // 数据埋点
      // 判断是图文还是纯文字：有图片列表且不为空则是图文，否则是文字
      const hasImages =
        ugcList.value[itemIndex].DISPLAY_PIC_LIST &&
        ugcList.value[itemIndex].DISPLAY_PIC_LIST.length > 0 &&
        ugcList.value[itemIndex].DISPLAY_PIC_LIST.some(
          (img) => img && img.trim()
        );
      const contentType = hasImages ? "图文" : "文字";

      window.activityDataLayer.push({
        eventName: "content_liking",
        eventType: contentType,
        eventValue: JSON.stringify({
          content_likes: ugcList.value[itemIndex].LIKE_COUNT,
          player_account: ugcList.value[itemIndex].LOGIN_ACCOUNT,
          player_uid: ugcList.value[itemIndex].UID,
          content_creation_time: ugcList.value[itemIndex].CREATE_TIME,
        }),
        aid: window.activity_name,
        gameId: window.game_id,
      });
    } else {
      emit(
        "showTipMessage",
        `[${code}]${langData[Math.abs(+code)] || langData.commonTip}`
      );
    }
  } catch (error) {
    console.error("点赞操作失败:", error);
    emit("showTipMessage", langData.commonTip || "操作失败");
  }
};

const searchUgc = () => {
  loadUgcData(
    activeUgcTab.value,
    sortType.value,
    "",
    searchKeyword.value,
    1,
    12
  );
};

const clearSearch = () => {
  searchKeyword.value = "";
  loadUgcData(activeUgcTab.value, sortType.value, "", "", 1, 12);
};

const toggleFilterPanel = () => {
  window.activityDataLayer.push({
    eventName: "click_button",
    eventType: "筛选按钮",
    eventReason: "UGC创作",
    aid: window.activity_name,
    gameId: window.game_id,
  });
  showFilterPanel.value = !showFilterPanel.value;
};

// 切换UGC标签
const switchUgcTab = (tab) => {
  // 如果切换到不同的tab，先上报当前tab的停留时间
  if (activeUgcTab.value !== tab) {
    reportStayTime(activeUgcTab.value);
    // 重置开始时间
    viewPageStartTime.value = new Date();
  }

  activeUgcTab.value = tab;
  searchKeyword.value = "";
  showFilterPanel.value = false;
  window.activityDataLayer.push({
    eventName: "click_button",
    eventType: getUgcTypeText(tab),
    eventReason: "UGC创作",
    aid: window.activity_name,
    gameId: window.game_id,
  });
  loadUgcData(tab, "new", "", "", 1, 12);
};

// 按钮点击事件
const showRulePopup = (content) => {
  // 可以传入自定义文案，也可以不传使用默认文案
  window.activityDataLayer.push({
    eventName: "click_button",
    eventType: "规则说明",
    eventReason: "UGC创作",
    aid: window.activity_name,
    gameId: window.game_id,
  });
  showRulePopupFn(content);
};

const jumpToWebsite = () => {
  emit("jumpToWebsite");
};

const jumpToFacebook = () => {
  emit("jumpToFacebook");
};

const jumpToDiscord = () => {
  emit("jumpToDiscord");
};

const selectFilterType = (type) => {
  selectedFilterType.value = type;
};

const setSortType = (type) => {
  sortType.value = type;
  loadUgcData(
    activeUgcTab.value,
    type,
    timeFilter.value,
    searchKeyword.value,
    1,
    12
  );
};

const setTimeFilter = (filter) => {
  let searchTime = "";
  if (filter === "day") {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    searchTime = new Date(today.getTime() - today.getTimezoneOffset() * 60000)
      .toISOString()
      .replace("T", " ")
      .substring(0, 19);
  } else if (filter === "week") {
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    weekAgo.setHours(0, 0, 0, 0);
    searchTime = new Date(
      weekAgo.getTime() - weekAgo.getTimezoneOffset() * 60000
    )
      .toISOString()
      .replace("T", " ")
      .substring(0, 19);
  }

  timeFilter.value = filter;
  loadUgcData(
    activeUgcTab.value,
    sortType.value,
    searchTime,
    searchKeyword.value,
    1,
    12
  );
};

const resetFilters = () => {
  sortType.value = "new";
  timeFilter.value = "";
  loadUgcData(activeUgcTab.value, "new", "", searchKeyword.value, 1, 12);
};

const resetFilter = () => {
  selectedFilterType.value = "all";
  searchKeyword.value = "";
  sortType.value = "new";
  timeFilter.value = "";
  loadUgcData(activeUgcTab.value, "new", "", "", 1, 12);
};

const applyFilter = () => {
  showFilterPanel.value = false;
};

// 生命周期
onMounted(() => {
  viewPageStartTime.value = new Date();
  loadUgcData(activeUgcTab.value);
});

onUnmounted(() => {
  reportStayTime();
});
</script>

<style scoped>
/* 如果全局样式没有正确应用，可以添加以下样式作为备用 */
.ugc-list {
  padding-right: 15px;
  padding-left: 15px;
  box-sizing: border-box;
  width: 1335px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px 15px;
  min-height: 562px;
}

/* 移动端样式 */
@media (max-width: 768px) {
  .ugc-list {
    width: 690px;
    grid-template-columns: repeat(2, 1fr);
    gap: 30px 30px;
  }
}
</style>
